import { useEffect, useRef, type KeyboardEvent } from "react";
import { LayoutGrid } from "lucide-react";
import { useTranslation } from "@thyra/landing-i18n";
import { nextChipIndex, type CatalogCategory } from "../../lib/agent-catalog";
import { centeredScrollLeft } from "../../lib/carousel-math";
import { LandingAgentIcon } from "../landing-agent-icon";
import { ScrollArrow } from "./ScrollArrow";
import { prefersReducedMotion } from "../../lib/motion";
import { useScrollEdges } from "./useScrollEdges";

type AgentCategoryChipsProps = {
  categories: CatalogCategory[];
  totalCount: number;
  /** null = All */
  selected: string | null;
  onSelect: (slug: string | null) => void;
};

/** Fraction of the visible row one arrow press travels. */
const PAGE_FRACTION = 0.75;

/**
 * Single-choice filter on ONE row that scrolls sideways (own previous/next buttons, edge fades). One tab
 * stop (roving tabindex); arrows, Home and End move and select, which is cheap because filtering is
 * instant and client-side. The selected chip is kept in view.
 */
export function AgentCategoryChips({
  categories,
  totalCount,
  selected,
  onSelect,
}: AgentCategoryChipsProps) {
  const { t } = useTranslation("sections");
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const { ref: scrollerRef, edges } = useScrollEdges<HTMLDivElement>(
    categories.length,
  );

  const options: { slug: string | null; label: string; count: number }[] = [
    { slug: null, label: t("assistantRoles.allCategories"), count: totalCount },
    ...categories.map((c) => ({
      slug: c.slug,
      label: c.label,
      count: c.count,
    })),
  ];
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.slug === selected),
  );

  // Bring the selected chip to the middle of the row (also after a click on a half-hidden one). The row
  // is scrolled directly: scrollIntoView could also move the page. Skipped on first paint.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const scroller = scrollerRef.current;
    const chip = refs.current[selectedIndex];
    if (!scroller || !chip) return;
    scroller.scrollTo({
      left: centeredScrollLeft(
        chip.offsetLeft,
        chip.offsetWidth,
        scroller.clientWidth,
        scroller.scrollWidth,
      ),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [selectedIndex, scrollerRef]);

  function page(direction: -1 | 1) {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * scroller.clientWidth * PAGE_FRACTION,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = nextChipIndex(index, event.key, options.length);
    if (next === null) return;
    event.preventDefault();
    onSelect(options[next].slug);
    const el = refs.current[next];
    el?.focus({ preventScroll: true });
  }

  return (
    <div className="flex items-center gap-2">
      <div
        className={`hidden sm:block ${edges.overflowing ? "" : "invisible"}`}
      >
        <ScrollArrow
          direction="previous"
          size="sm"
          label={t("assistantRoles.categoriesPrevious")}
          inactive={edges.atStart}
          onPress={() => page(-1)}
        />
      </div>
      <div
        ref={scrollerRef}
        role="radiogroup"
        aria-label={t("assistantRoles.categoriesLabel")}
        data-fade-start={edges.overflowing && !edges.atStart}
        data-fade-end={edges.overflowing && !edges.atEnd}
        className="agent-scroll edge-fade relative -my-1 flex min-w-0 flex-1 flex-nowrap gap-2 overflow-x-auto overscroll-x-contain px-1 py-1"
      >
        {options.map((option, index) => {
          const active = index === selectedIndex;
          return (
            <button
              key={option.slug ?? "__all"}
              ref={(el) => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onSelect(option.slug)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:min-h-10 ${
                active
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-card text-muted-foreground-strong hover:border-primary/40 hover:text-foreground"
              }`}
            >
              {option.slug === null ? (
                <LayoutGrid className="h-4 w-4" aria-hidden />
              ) : (
                <LandingAgentIcon
                  iconKey={categories[index - 1]?.iconKey}
                  className="h-4 w-4"
                />
              )}
              <span>{option.label}</span>
              <span
                className={`rounded-full px-1.5 text-xs font-semibold tabular-nums ${
                  active
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground-strong"
                }`}
              >
                {option.count}
              </span>
            </button>
          );
        })}
      </div>
      <div
        className={`hidden sm:block ${edges.overflowing ? "" : "invisible"}`}
      >
        <ScrollArrow
          direction="next"
          size="sm"
          label={t("assistantRoles.categoriesNext")}
          inactive={edges.atEnd}
          onPress={() => page(1)}
        />
      </div>
    </div>
  );
}

export function AgentCategoryChipsSkeleton() {
  return (
    <div aria-hidden className="flex gap-2 overflow-hidden py-1 sm:px-11">
      {[88, 104, 96, 112, 92, 100].map((w, i) => (
        <div
          key={i}
          className="h-11 shrink-0 animate-pulse rounded-full bg-muted sm:h-10"
          style={{ width: w }}
        />
      ))}
    </div>
  );
}
